import { useQuery } from '@tanstack/react-query';
import { startOfWeek, endOfWeek } from 'date-fns';
import { scheduleKeys } from './queryKeys';
import { fetchOpenShifts } from '@/services/schedule/shiftService';
import { isOffersEnabled } from '@/hooks/useCompanySettings';
import { Database } from '@/integrations/supabase/types';

type OpenShiftDetails = Database["public"]["CompositeTypes"]["open_shift_details"];

/**
 * Hook to fetch open shifts for the current week
 * 
 * POLLING REMOVED: This hook no longer polls. Real-time updates are handled by
 * useOpenShiftsRealtimeSubscription via OpenShiftsRealtimeProvider.
 * 
 * The provider subscribes to Supabase Realtime and invalidates this query
 * when open shift changes are detected, eliminating the need for polling.
 * 
 * @see src/hooks/schedule/useOpenShiftsRealtimeSubscription.ts
 * @see src/providers/OpenShiftsRealtimeProvider.tsx
 */
export const useOpenShifts = (
  companyId: string | undefined, 
  currentWeek: Date, 
  profileRole: string | undefined,
  companySettings: any
) => {
  const weekStartISO = startOfWeek(currentWeek).toISOString();
  const weekEndISO = endOfWeek(currentWeek).toISOString();
  
  // All authenticated users can claim shifts - feature is gated by company settings
  const offersEnabled = isOffersEnabled(companySettings);
  
  return useQuery({
    queryKey: scheduleKeys.openShifts(companyId || '', weekStartISO, weekEndISO),
    queryFn: () => fetchOpenShifts(weekStartISO, weekEndISO),
    enabled: offersEnabled && !!companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true, // Fallback: refetch when user returns to tab
    refetchOnReconnect: true, // Fallback: refetch when network reconnects
    // POLLING REMOVED - Realtime subscription handles updates
    // Previously: refetchInterval: 60 * 1000 when data.length > 0
    retry: (failureCount, error: any) => {
      // Don't retry if it's an authentication/authorization error (400/401/403)
      if (error?.message?.includes('unauthorized') || 
          (error?.code && ['PGRST301', 'PGRST302'].includes(error.code)) ||
          (error?.status && [400, 401, 403].includes(error.status))) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};
